import React, { useState, useEffect, useRef, useMemo } from 'react';
import { api } from '../api';
import { useCustomer } from '../contexts/CustomerContext';
import AdminDashboard from './AdminDashboard';
import AdminHourlyMeters from './AdminHourlyMeters';
import {
  Briefcase, Car, CurrencyDollar, Percent, UsersThree, Clock,
} from '@phosphor-icons/react';

// ── Week helpers ──────────────────────────────────────────────────────────────
function getCurrentWeekNumber() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const diff = now - start + (start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000;
  const oneWeek = 1000 * 60 * 60 * 24 * 7;
  return Math.ceil(diff / oneWeek);
}

// ── Catmull-Rom → Bezier path builder (smooth line) ───────────────────────────
function smoothPath(points) {
  if (points.length < 2) return '';
  if (points.length === 2) return `M${points[0].x},${points[0].y} L${points[1].x},${points[1].y}`;
  const d = [`M${points[0].x},${points[0].y}`];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d.push(`C${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`);
  }
  return d.join(' ');
}

// ── SVG Line Chart (Weekly Gross YoY with interactive tooltip) ────────────────
const YEAR_COLORS = {
  2023: '#CBD5E1',
  2024: '#94A3B8',
  2025: '#4A7A91',
  2026: '#F5C518',
  2027: '#16A34A',
};

function WeeklyGrossChart({ data, currentWeek, visibleYears }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(900);
  const [hoverWeek, setHoverWeek] = useState(null);

  useEffect(() => {
    function measure() {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const PADDING = { top: 24, right: 24, bottom: 44, left: 58 };
  const height = 320;
  const chartW = Math.max(100, width - PADDING.left - PADDING.right);
  const chartH = height - PADDING.top - PADDING.bottom;

  const byYear = {};
  data.forEach(d => {
    if (!byYear[d.year]) byYear[d.year] = {};
    byYear[d.year][d.week] = d.gross;
  });
  const allYears = Object.keys(byYear).map(Number).sort();
  // Filter by visibleYears Set (if provided); otherwise show all
  const years = visibleYears && visibleYears.size > 0
    ? allYears.filter(y => visibleYears.has(y))
    : allYears;

  // Scale Y-axis using only the years actually being shown
  const visibleData = data.filter(d => years.includes(d.year));
  const allGross = visibleData.map(d => d.gross);
  const maxGross = allGross.length > 0 ? Math.max(...allGross) : 1000;
  const minGross = 0;
  const WEEKS = 52;

  function xPos(week) {
    return PADDING.left + ((week - 1) / (WEEKS - 1)) * chartW;
  }
  function yPos(gross) {
    return PADDING.top + chartH - ((gross - minGross) / (maxGross - minGross || 1)) * chartH;
  }

  const yTicks = 5;
  const yTickVals = Array.from({ length: yTicks + 1 }, (_, i) =>
    Math.round((maxGross / yTicks) * i)
  );
  const xTicks = [1, 5, 9, 13, 17, 21, 25, 29, 33, 37, 41, 45, 49, 52];

  function handleMove(e) {
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const relX = x - PADDING.left;
    if (relX < 0 || relX > chartW) { setHoverWeek(null); return; }
    const w = Math.round(1 + (relX / chartW) * (WEEKS - 1));
    setHoverWeek(Math.min(52, Math.max(1, w)));
  }

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <svg
        ref={svgRef}
        width={width} height={height}
        style={{ display: 'block', fontFamily: 'inherit' }}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverWeek(null)}
      >
        {/* Horizontal grid */}
        {yTickVals.map(v => (
          <line key={v} x1={PADDING.left} x2={PADDING.left + chartW}
            y1={yPos(v)} y2={yPos(v)} stroke="#F1F5F9" strokeWidth={1} />
        ))}

        {/* Y labels */}
        {yTickVals.map(v => (
          <text key={v} x={PADDING.left - 10} y={yPos(v) + 4}
            textAnchor="end" fontSize={10} fill="#94A3B8" fontWeight={500}>
            ${v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : v}
          </text>
        ))}

        {/* X labels */}
        {xTicks.map(w => (
          <text key={w} x={xPos(w)} y={PADDING.top + chartH + 20}
            textAnchor="middle" fontSize={10} fill="#94A3B8" fontWeight={500}>W{w}</text>
        ))}

        {/* Smooth lines */}
        {years.map(year => {
          const weekData = byYear[year];
          const points = Object.keys(weekData).map(Number).sort((a, b) => a - b)
            .filter(w => w >= 1 && w <= 52)
            .map(w => ({ x: xPos(w), y: yPos(weekData[w]), w, gross: weekData[w] }));
          if (points.length === 0) return null;
          const stroke = YEAR_COLORS[year] || '#6B7280';
          return (
            <g key={year}>
              <path d={smoothPath(points)} fill="none" stroke={stroke} strokeWidth={2.5}
                strokeLinejoin="round" strokeLinecap="round" />
              {points.map(p => (
                <circle key={p.w} cx={p.x} cy={p.y}
                  r={hoverWeek === p.w ? 5 : 3}
                  fill="#fff" stroke={stroke} strokeWidth={2} />
              ))}
            </g>
          );
        })}

        {/* Hover line */}
        {hoverWeek && (
          <line
            x1={xPos(hoverWeek)} x2={xPos(hoverWeek)}
            y1={PADDING.top} y2={PADDING.top + chartH}
            stroke="#0F2233" strokeWidth={1} strokeDasharray="3 3" opacity={0.4}
          />
        )}
      </svg>

      {/* Interactive tooltip */}
      {hoverWeek && (() => {
        const tipX = xPos(hoverWeek);
        const vals = years.map(y => ({ year: y, val: byYear[y]?.[hoverWeek] }))
          .filter(v => v.val != null);
        if (vals.length === 0) return null;
        const left = Math.min(width - 170, Math.max(8, tipX + 12));
        return (
          <div style={{
            position: 'absolute', left, top: PADDING.top,
            background: '#0F2233', color: '#fff',
            padding: '10px 12px', borderRadius: 10, fontSize: 12,
            boxShadow: '0 10px 25px rgba(15,34,51,0.25)',
            pointerEvents: 'none', minWidth: 140,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#F5C518', marginBottom: 6, letterSpacing: 0.5 }}>
              W{hoverWeek}
            </div>
            {vals.map(v => (
              <div key={v.year} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '2px 0' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: YEAR_COLORS[v.year] }} />
                  <span style={{ color: '#CBD5E1' }}>{v.year}</span>
                </span>
                <strong>${v.val >= 1000 ? `${(v.val / 1000).toFixed(1)}k` : v.val.toFixed(0)}</strong>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Current week indicator (legend is rendered outside as toggle pills) */}
      {currentWeek >= 1 && currentWeek <= 52 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, paddingLeft: PADDING.left }}>
          <span style={{ fontSize: 11, color: '#94A3B8' }}>Current:</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#DC2626' }}>W{currentWeek}</span>
        </div>
      )}
    </div>
  );
}

// ── Year toggle pills (sits above the Weekly Gross YoY chart) ────────────────
function YearTogglePills({ allYears, visibleYears, onToggle }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {allYears.map(y => {
        const active = visibleYears.has(y);
        const color = YEAR_COLORS[y] || '#6B7280';
        return (
          <button
            key={y}
            type="button"
            onClick={() => onToggle(y)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '5px 11px',
              borderRadius: 999,
              border: active ? `1.5px solid ${color}` : '1.5px solid #E2E8F0',
              background: active ? `${color}14` : '#fff',
              color: active ? '#0F2233' : '#94A3B8',
              fontSize: 12, fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 120ms ease',
              opacity: active ? 1 : 0.7,
            }}
          >
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: active ? color : '#CBD5E1',
              display: 'inline-block',
            }} />
            {y}
          </button>
        );
      })}
    </div>
  );
}

// ── Jobs by Service Bar Chart (current-week breakdown — count + revenue) ─────
const DEPT_COLORS = {
  'OFFICE':      '#4A7A91',
  'SERVICE':     '#3B82F6',
  'USED CARS':   '#A855F7',
  'KIA CARS':    '#F5C518',
  'SUZUKI CARS': '#EF4444',
  'GWM CARS':    '#16A34A',
  'GAC CARS':    '#EA580C',
  'Unassigned':  '#94A3B8',
};

function departmentColor(dept) {
  if (DEPT_COLORS[dept]) return DEPT_COLORS[dept];
  // deterministic fallback from name
  let h = 0;
  for (let i = 0; i < dept.length; i++) h = (h * 31 + dept.charCodeAt(i)) >>> 0;
  const palette = ['#4A7A91', '#3B82F6', '#A855F7', '#F5C518', '#EF4444', '#16A34A', '#EC4899', '#F97316'];
  return palette[h % palette.length];
}

// Top services to render — anything beyond this gets bucketed into "Other".
// 8 keeps the card height in line with the other two cards in the three-up.
const SERVICE_TOP_N = 8;

function JobsByServiceBars({ rows, weekNumber, loading }) {
  // rows: [{ service, department, count, revenue }] — backend sorts by revenue
  const data = useMemo(() => {
    const filtered = (rows || []).filter(r => (r.count || 0) > 0 || (r.revenue || 0) > 0);
    if (filtered.length <= SERVICE_TOP_N) return filtered;
    const top = filtered.slice(0, SERVICE_TOP_N);
    const rest = filtered.slice(SERVICE_TOP_N);
    const otherCount   = rest.reduce((s, r) => s + (r.count   || 0), 0);
    const otherRevenue = rest.reduce((s, r) => s + (r.revenue || 0), 0);
    return [
      ...top,
      { service: `+ ${rest.length} more`, department: 'Unassigned', count: otherCount, revenue: otherRevenue, isOther: true },
    ];
  }, [rows]);

  const totalJobs    = (rows || []).reduce((s, r) => s + (r.count   || 0), 0);
  const totalRevenue = (rows || []).reduce((s, r) => s + (r.revenue || 0), 0);
  // Each bar is normalised to its own max — that way the longest revenue bar
  // and the longest count bar both fill their lanes, regardless of scale.
  const maxCount   = Math.max(1, ...data.map(d => d.count   || 0));
  const maxRevenue = Math.max(1, ...data.map(d => d.revenue || 0));

  const fmtMoney = v => `$${Math.round(v || 0).toLocaleString()}`;

  return (
    <div className="v2-card-lg">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0F2233' }}>
          Jobs by Service
        </div>
        {!loading && totalJobs > 0 && (
          <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>
            {totalJobs} jobs · {fmtMoney(totalRevenue)}
          </div>
        )}
      </div>
      <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 14 }}>
        {weekNumber ? `Current week (W${weekNumber})` : 'Current week'}
      </div>

      {/* Tiny legend */}
      {!loading && data.length > 0 && (
        <div style={{ display: 'flex', gap: 14, marginBottom: 10, fontSize: 10, color: '#64748B', fontWeight: 600 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 8, background: '#0F2233', display: 'inline-block', borderRadius: 1 }} />
            REVENUE
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 8, background: '#94A3B8', display: 'inline-block', borderRadius: 1, opacity: 0.55 }} />
            JOBS
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94A3B8', fontSize: 13 }}>
          Loading…
        </div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94A3B8', fontSize: 13 }}>
          No jobs for this week.
        </div>
      ) : (
        <div>
          {data.map((row, i) => {
            const color = departmentColor(row.department);
            const revPct   = (row.revenue || 0) / maxRevenue;
            const countPct = (row.count   || 0) / maxCount;
            const revShare = totalRevenue > 0 ? (row.revenue || 0) / totalRevenue : 0;
            return (
              <div key={`${row.service}-${i}`} style={{
                padding: '8px 0',
                borderBottom: i < data.length - 1 ? '1px solid #F1F5F9' : 'none',
              }}>
                {/* Header line: service name (left) · counts + $ (right) */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
                  <div style={{
                    flex: 1, minWidth: 0,
                    fontSize: 11.5, fontWeight: 700, color: row.isOther ? '#94A3B8' : '#0F2233',
                    fontStyle: row.isOther ? 'italic' : 'normal',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }} title={row.service}>
                    {row.service}
                  </div>
                  <div style={{ fontSize: 10, color: '#64748B', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {row.count} {row.count === 1 ? 'job' : 'jobs'}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 800, color, whiteSpace: 'nowrap', minWidth: 56, textAlign: 'right' }}>
                    {fmtMoney(row.revenue)}
                  </div>
                </div>
                {/* Revenue bar — primary, full saturation */}
                <div style={{ height: 8, background: '#F1F5F9', borderRadius: 2, overflow: 'hidden', marginBottom: 3 }}>
                  <div style={{
                    width: `${Math.max(2, revPct * 100)}%`,
                    height: '100%',
                    background: color,
                    transition: 'width 0.3s ease',
                  }} />
                </div>
                {/* Jobs bar — secondary, muted */}
                <div style={{ height: 5, background: '#F8FAFC', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.max(2, countPct * 100)}%`,
                    height: '100%',
                    background: color,
                    opacity: 0.4,
                    transition: 'width 0.3s ease',
                  }} />
                </div>
                {/* % of total revenue — small caption */}
                {!row.isOther && revShare > 0 && (
                  <div style={{ fontSize: 9, color: '#94A3B8', fontWeight: 600, marginTop: 3 }}>
                    {(revShare * 100).toFixed(1)}% of revenue
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Revenue Mix Donut Chart (no center dollar, colored legend) ────────────────
function RevenueMixDonut({ kpis }) {
  const totals = useMemo(() => {
    const gross = kpis.reduce((s, k) => s + (k.gross || 0), 0);
    const staff = kpis.reduce((s, k) => s + (k.staff_payout || 0), 0);
    const gst   = kpis.reduce((s, k) => s + (k.gst || 0), 0);
    const netProfit = kpis.reduce((s, k) => s + (k.net_profit || 0), 0);
    const directorShare = Math.max(0, gross - staff - gst - netProfit);
    return { gross, staff, gst, netProfit, directorShare };
  }, [kpis]);

  const segments = [
    { label: 'Staff Payout',   value: totals.staff,                          color: '#EF4444' },
    { label: 'Director Share', value: totals.directorShare,                  color: '#4A7A91' },
    { label: 'Net Profit',     value: Math.max(0, totals.netProfit),         color: '#16A34A' },
    { label: 'GST',            value: totals.gst,                            color: '#F5C518' },
  ].filter(s => s.value > 0);

  const sum = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  const size = 200;
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = 90;
  const rInner = 62;

  let startAngle = -Math.PI / 2;
  const arcs = segments.map(seg => {
    const frac = seg.value / sum;
    const endAngle = startAngle + frac * 2 * Math.PI;
    const largeArc = frac > 0.5 ? 1 : 0;
    const x1 = cx + rOuter * Math.cos(startAngle);
    const y1 = cy + rOuter * Math.sin(startAngle);
    const x2 = cx + rOuter * Math.cos(endAngle);
    const y2 = cy + rOuter * Math.sin(endAngle);
    const x3 = cx + rInner * Math.cos(endAngle);
    const y3 = cy + rInner * Math.sin(endAngle);
    const x4 = cx + rInner * Math.cos(startAngle);
    const y4 = cy + rInner * Math.sin(startAngle);
    const d = [
      `M ${x1} ${y1}`,
      `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4}`,
      'Z',
    ].join(' ');
    const result = { ...seg, d, frac };
    startAngle = endAngle;
    return result;
  });

  return (
    <div className="v2-card-lg">
      <div style={{ fontSize: 16, fontWeight: 800, color: '#0F2233', marginBottom: 4 }}>
        Revenue Mix
      </div>
      <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 16 }}>
        Last {kpis.length} weeks
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        <svg width={size} height={size} style={{ flexShrink: 0 }}>
          {arcs.map((a, i) => (
            <path key={i} d={a.d} fill={a.color} stroke="#fff" strokeWidth={2} />
          ))}
        </svg>

        <div style={{ flex: 1, minWidth: 160 }}>
          {arcs.map((a, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 0',
              borderBottom: i < arcs.length - 1 ? '1px solid #F1F5F9' : 'none',
            }}>
              <div style={{
                width: 10, height: 10, background: a.color,
                borderRadius: '50%', flexShrink: 0,
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0F2233' }}>{a.label}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: a.color }}>
                {(a.frac * 100).toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Net Profit Over Time (blue gradient, smooth curves, rounded) ──────────────
function NetProfitAreaChart({ kpis }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(600);
  const [hoverIdx, setHoverIdx] = useState(null);

  useEffect(() => {
    function measure() {
      if (containerRef.current) setWidth(containerRef.current.offsetWidth);
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const PADDING = { top: 20, right: 20, bottom: 34, left: 54 };
  const height = 240;
  const chartW = Math.max(100, width - PADDING.left - PADDING.right);
  const chartH = height - PADDING.top - PADDING.bottom;

  const values = kpis.map(k => k.net_profit || 0);
  const maxV = Math.max(1, ...values.map(v => Math.max(v, 0)));
  const minV = Math.min(0, ...values.map(v => Math.min(v, 0)));
  const range = maxV - minV || 1;

  const xPos = (i) => PADDING.left + (i / Math.max(1, kpis.length - 1)) * chartW;
  const yPos = (v) => PADDING.top + chartH - ((v - minV) / range) * chartH;
  const y0   = yPos(0);

  const points = kpis.map((k, i) => ({ x: xPos(i), y: yPos(k.net_profit || 0), k, i }));
  const linePath = smoothPath(points);
  const areaPath = points.length > 0
    ? `${linePath} L${xPos(points.length - 1)},${y0} L${xPos(0)},${y0} Z`
    : '';

  const yTicks = [minV, minV + range * 0.5, maxV];

  function handleMove(e) {
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const relX = x - PADDING.left;
    if (relX < 0 || relX > chartW || kpis.length === 0) { setHoverIdx(null); return; }
    const idx = Math.round((relX / chartW) * (kpis.length - 1));
    setHoverIdx(Math.min(kpis.length - 1, Math.max(0, idx)));
  }

  return (
    <div className="v2-card-lg">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0F2233' }}>
          Net Profit Over Time
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 12 }}>
        Last {kpis.length} weeks
      </div>

      <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
        <svg
          ref={svgRef}
          width={width} height={height}
          style={{ display: 'block' }}
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          <defs>
            <linearGradient id="npGradBlue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="#3B82F6" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {yTicks.map((v, i) => (
            <g key={i}>
              <line x1={PADDING.left} x2={PADDING.left + chartW} y1={yPos(v)} y2={yPos(v)} stroke="#F1F5F9" strokeWidth={1} />
              <text x={PADDING.left - 8} y={yPos(v) + 3} textAnchor="end" fontSize={10} fill="#94A3B8" fontWeight={500}>
                ${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : Math.round(v)}
              </text>
            </g>
          ))}

          {areaPath && <path d={areaPath} fill="url(#npGradBlue)" />}
          {linePath && <path d={linePath} fill="none" stroke="#3B82F6" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />}

          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y}
              r={hoverIdx === i ? 5 : 3}
              fill="#fff" stroke="#3B82F6" strokeWidth={2} />
          ))}

          {kpis.map((k, i) => (i % Math.ceil(kpis.length / 6) === 0 || i === kpis.length - 1) && (
            <text key={i} x={xPos(i)} y={height - 12} textAnchor="middle" fontSize={10} fill="#94A3B8" fontWeight={500}>W{k.week_number}</text>
          ))}

          {hoverIdx != null && (
            <line
              x1={xPos(hoverIdx)} x2={xPos(hoverIdx)}
              y1={PADDING.top} y2={PADDING.top + chartH}
              stroke="#0F2233" strokeWidth={1} strokeDasharray="3 3" opacity={0.4}
            />
          )}
        </svg>

        {hoverIdx != null && (() => {
          const k = kpis[hoverIdx];
          const tipX = xPos(hoverIdx);
          const left = Math.min(width - 140, Math.max(8, tipX + 12));
          return (
            <div style={{
              position: 'absolute', left, top: PADDING.top,
              background: '#0F2233', color: '#fff',
              padding: '10px 12px', borderRadius: 10, fontSize: 12,
              boxShadow: '0 10px 25px rgba(15,34,51,0.25)',
              pointerEvents: 'none', minWidth: 110,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#F5C518', marginBottom: 4 }}>
                W{k.week_number}
              </div>
              <div style={{ fontSize: 14, fontWeight: 800 }}>
                ${(k.net_profit || 0).toFixed(0)}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ── Sparkline (smooth mini line) ──────────────────────────────────────────────
function Sparkline({ values, color = '#3B82F6', width = 110, height = 34 }) {
  if (!values || values.length === 0) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = (max - min) || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const pts = values.map((v, i) => ({
    x: i * step,
    y: height - ((v - min) / range) * (height - 6) - 3,
  }));
  const pathD = smoothPath(pts);
  const last = pts[pts.length - 1];
  const gradId = `sparkGrad_${color.replace('#', '')}`;
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {pts.length > 1 && (
        <path
          d={`${pathD} L${last.x},${height} L${pts[0].x},${height} Z`}
          fill={`url(#${gradId})`}
        />
      )}
      <path d={pathD} fill="none" stroke={color} strokeWidth={1.8}
        strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last.x} cy={last.y} r={2.5} fill={color} />
    </svg>
  );
}

// ── Minimal borderless sparkline card ─────────────────────────────────────────
function SparkCard({ icon: Icon, label, value, subvalue, values, color, delta }) {
  return (
    <div style={{
      flex: '1 1 210px', minWidth: 210,
      background: '#fff',
      borderRadius: 16,
      padding: '16px 18px',
      boxShadow: '0 1px 3px rgba(15,34,51,0.04), 0 4px 12px rgba(15,34,51,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: `${color}1A`, color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={18} color={color} weight="fill" />
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: 0.3 }}>{label}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#0F2233', whiteSpace: 'nowrap', letterSpacing: -0.3 }}>{value}</div>
          {subvalue && (
            <div style={{
              fontSize: 11, fontWeight: 700, marginTop: 4,
              color: delta >= 0 ? '#16A34A' : '#DC2626',
            }}>
              {delta >= 0 ? '▲' : '▼'} {subvalue}
            </div>
          )}
        </div>
        <Sparkline values={values} color={color} width={100} height={34} />
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AdminDashboardHome({ user }) {
  // Scope the dashboard to the active workspace — the private / walk-in
  // customer's jobs were being counted into Melville's numbers.
  const { customerParam } = useCustomer();
  const currentWeek = getCurrentWeekNumber();
  const currentYear = new Date().getFullYear();
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState(null);

  const [kpis, setKpis] = useState([]);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [kpisError, setKpisError] = useState(null);

  // Per-year visibility toggle for the Weekly Gross YoY chart
  // Default: show only current and previous year (older ones hidden)
  const [visibleYears, setVisibleYears] = useState(
    () => new Set([currentYear - 1, currentYear])
  );

  // Current-week jobs breakdown (for the Jobs by Department donut)
  const [jobsBreakdown, setJobsBreakdown] = useState([]);
  const [jobsBreakdownLoading, setJobsBreakdownLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        setChartLoading(true);
        setChartError(null);
        // Fetch current year + 3 previous years so the toggle has material to work with
        const years = [currentYear - 3, currentYear - 2, currentYear - 1, currentYear];
        const rows = await api.weeklyGross(years, customerParam);
        setChartData(Array.isArray(rows) ? rows : []);
      } catch (err) {
        setChartError(err.message);
        setChartData([]);
      } finally {
        setChartLoading(false);
      }
    }
    load();
  }, [currentYear, customerParam]);

  useEffect(() => {
    async function loadKpis() {
      try {
        setKpisLoading(true);
        setKpisError(null);
        const rows = await api.getWeeklyKPIs(12, customerParam);
        setKpis(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error('Failed to load weekly KPIs:', err);
        setKpisError(err.message || 'Failed to load KPIs');
        setKpis([]);
      } finally {
        setKpisLoading(false);
      }
    }
    loadKpis();
  }, [customerParam]);

  useEffect(() => {
    async function loadBreakdown() {
      try {
        setJobsBreakdownLoading(true);
        const data = await api.getWeeklyJobsBreakdown(currentWeek, currentYear, customerParam);
        setJobsBreakdown(Array.isArray(data?.services) ? data.services : []);
      } catch {
        setJobsBreakdown([]);
      } finally {
        setJobsBreakdownLoading(false);
      }
    }
    loadBreakdown();
  }, [currentWeek, currentYear, customerParam]);

  // Years actually present in the dataset (sorted ascending)
  const availableYears = useMemo(() => {
    const s = new Set();
    chartData.forEach(d => s.add(d.year));
    return [...s].sort();
  }, [chartData]);

  function toggleYear(y) {
    setVisibleYears(prev => {
      const next = new Set(prev);
      if (next.has(y)) next.delete(y); else next.add(y);
      // Never allow the chart to go fully empty — keep at least one year on
      if (next.size === 0) next.add(y);
      return next;
    });
  }

  const stats = useMemo(() => {
    if (kpis.length < 2) return null;
    const curr = kpis[kpis.length - 1];
    const prev = kpis[kpis.length - 2];
    const pct = (c, p) => (p > 0 ? ((c - p) / p) * 100 : (c > 0 ? 100 : 0));
    return {
      curr,
      prev,
      jobsDelta:        pct(curr.jobs_count, prev.jobs_count),
      avgJobDelta:      pct(curr.avg_job_value, prev.avg_job_value),
      grossMarginDelta: curr.gross_margin - prev.gross_margin,
      netMarginDelta:   curr.net_margin - prev.net_margin,
      hoursDelta:       pct(curr.hours_worked, prev.hours_worked),
    };
  }, [kpis]);

  const fmtDelta = (n, suffix = '%') => `${Math.abs(n).toFixed(1)}${suffix}`;

  // Section order per the handoff: KPIs → payroll → coverage → analytics →
  // revenue by department → trends → YoY. Coverage and the analytics trio are
  // injected into AdminDashboard's overview so they land above the department
  // breakdown instead of two screens below it.
  const coverageAndAnalytics = (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px' }}>
      <AdminHourlyMeters />

      {kpisLoading && (
        <div className="v2-card-lg" style={{ textAlign: 'center', padding: '60px 0', color: '#94A3B8', fontSize: 13, marginBottom: 16 }}>
          Loading analytics…
        </div>
      )}
      {!kpisLoading && kpisError && (
        <div className="v2-card-lg" style={{ textAlign: 'center', padding: '40px 24px', color: '#DC2626', fontSize: 13, marginBottom: 16 }}>
          Could not load analytics: {kpisError}
        </div>
      )}
      {!kpisLoading && !kpisError && kpis.length === 0 && (
        <div className="v2-card-lg" style={{ textAlign: 'center', padding: '40px 24px', color: '#94A3B8', fontSize: 13, marginBottom: 16 }}>
          No weekly KPI data available yet.
        </div>
      )}
      {!kpisLoading && !kpisError && kpis.length > 0 && (
        <div className="v2-charts-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.25fr) minmax(0, 1fr) minmax(0, 1fr)', gap: 16, marginBottom: 16 }}>
          <NetProfitAreaChart kpis={kpis} />
          <RevenueMixDonut kpis={kpis} />
          <JobsByServiceBars rows={jobsBreakdown} weekNumber={currentWeek} loading={jobsBreakdownLoading} />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <AdminDashboard user={user} view="overview" slotAfterPayroll={coverageAndAnalytics} />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px 16px' }}>
        {/* Sparkline KPI row (last 12w trends) */}
        {!kpisLoading && stats && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0F2233', marginBottom: 10 }}>
              Trends · Last 12 weeks
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <SparkCard
                icon={Briefcase}
                label="JOBS COMPLETED"
                value={stats.curr.jobs_count}
                subvalue={fmtDelta(stats.jobsDelta)}
                delta={stats.jobsDelta}
                values={kpis.map(k => k.jobs_count)}
                color="#4A7A91"
              />
              <SparkCard
                icon={Car}
                label="AVG JOB VALUE"
                value={`$${stats.curr.avg_job_value.toFixed(0)}`}
                subvalue={fmtDelta(stats.avgJobDelta)}
                delta={stats.avgJobDelta}
                values={kpis.map(k => k.avg_job_value)}
                color="#16A34A"
              />
              <SparkCard
                icon={CurrencyDollar}
                label="GROSS MARGIN"
                value={`${stats.curr.gross_margin.toFixed(1)}%`}
                subvalue={fmtDelta(stats.grossMarginDelta, ' pp')}
                delta={stats.grossMarginDelta}
                values={kpis.map(k => k.gross_margin)}
                color="#F5C518"
              />
              <SparkCard
                icon={Percent}
                label="NET MARGIN"
                value={`${stats.curr.net_margin.toFixed(1)}%`}
                subvalue={fmtDelta(stats.netMarginDelta, ' pp')}
                delta={stats.netMarginDelta}
                values={kpis.map(k => k.net_margin)}
                color="#3B82F6"
              />
              <SparkCard
                icon={UsersThree}
                label="STAFF PAYOUT"
                value={`$${stats.curr.staff_payout >= 1000 ? `${(stats.curr.staff_payout / 1000).toFixed(1)}k` : stats.curr.staff_payout.toFixed(0)}`}
                subvalue={`W${stats.curr.week_number}`}
                delta={0}
                values={kpis.map(k => k.staff_payout)}
                color="#EF4444"
              />
              <SparkCard
                icon={Clock}
                label="HOURS WORKED"
                value={`${stats.curr.hours_worked.toFixed(1)}h`}
                subvalue={fmtDelta(stats.hoursDelta)}
                delta={stats.hoursDelta}
                values={kpis.map(k => k.hours_worked)}
                color="#A855F7"
              />
            </div>
          </div>
        )}

        {/* Weekly Gross YoY — history, so it closes the page rather than
            pushing the operational panels below the fold. */}
        <div className="v2-card-lg" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0F2233' }}>
                Weekly Gross YoY
              </div>
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
                Year-over-year comparison — click a year to show/hide
              </div>
            </div>
            {availableYears.length > 0 && (
              <YearTogglePills
                allYears={availableYears}
                visibleYears={visibleYears}
                onToggle={toggleYear}
              />
            )}
          </div>

          <div style={{ marginTop: 12 }}>
            {chartLoading && (
              <div style={{ textAlign: 'center', padding: '80px 0', color: '#94A3B8', fontSize: 13 }}>
                Loading chart data…
              </div>
            )}
            {!chartLoading && chartError && (
              <div style={{ textAlign: 'center', padding: '80px 0', color: '#DC2626', fontSize: 13 }}>
                Could not load chart: {chartError}
              </div>
            )}
            {!chartLoading && !chartError && (
              <WeeklyGrossChart data={chartData} currentWeek={currentWeek} visibleYears={visibleYears} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
